import {
  faCog as faCogOutlined,
  faDownload as faDownloadOutlined,
} from '@fortawesome/pro-regular-svg-icons';
import { faCog, faDownload } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { Route, useHistory, useRouteMatch } from 'react-router';
import { CancelDialogBag } from '../../../components/muiLegacy/Dialogs/CancelDialog/useCancelDialog';
import VoxelAppBar from '../../../components/voxel/AppBar/VoxelAppBar';
import VoxelAppBarDivider from '../../../components/voxel/AppBar/VoxelAppBarDivider';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import LogoSymbolsOnly from '../../../components/voxel/Logo/LogoSymbolsOnly';
import { VoxelPopover } from '../../../components/voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../components/voxel/Popover/VoxelPopoverTrigger';
import VoxelUnderlinedTabs from '../../../components/voxel/Tabs/VoxelUnderlinedTabs';
import VoxelTooltipShort from '../../../components/voxel/Tooltip/VoxelTooltipShort';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import DownloadsPopover from '../../DataManagementPage/components/Popovers/DownloadsPopover';
import { ProjectSettingsMenuChoices } from '../../ProjectSettingsPage/ProjectSettingsPage';
import { ProjectTabChoices } from '../ProjectPage';
import LanguageIndicator from './LanguageIndicator';
import LocationIndicator from './LocationIndicator';
import ProjectDomainIndicator from './ProjectDomainIndicator';
import ProjectNameIndicator from './ProjectNameIndicator';
import StatusIndicator from './StatusIndicator/StatusIndicator';

export interface ProjectAppBarProps {
  project: ProjectGq;
  cancelDialogBag?: CancelDialogBag;
  window?: () => Window;
  children?: React.ReactElement;
  user: CurrentUserGq;
  isScrolling?: boolean;
  drawerOpen?: boolean;
  downloadOpen?: boolean;
  className?: string;
}

export const ProjectAppBar: React.FC<ProjectAppBarProps> = ({
  project,
  user,
  cancelDialogBag,
  isScrolling,
  drawerOpen,
  downloadOpen,
  className,
}) => {
  // Navigation
  const history = useHistory();

  const goToTab = useMemo(() => {
    const action = (tab: ProjectTabChoices) => history.push(`/${project.id}/${tab}`);
    if (cancelDialogBag) {
      return cancelDialogBag.wrapAction(action);
    }
    return action;
  }, [project.id, cancelDialogBag]);

  const goToProjects = useMemo(() => {
    const action = () => history.push('/');
    if (cancelDialogBag) {
      return cancelDialogBag.wrapAction(action);
    }
    return action;
  }, [cancelDialogBag]);

  const goToSettings = useMemo(() => {
    const action = () =>
      history.push(
        `/${project.id}/${ProjectTabChoices.Settings}/${ProjectSettingsMenuChoices.General}`
      );
    if (cancelDialogBag) {
      return cancelDialogBag.wrapAction(action);
    }
    return action;
  }, [cancelDialogBag]);

  // Loading results
  const { result: loadingResults } = projectsApiService.queries.getProjectStatus(
    { projectId: project.id },
    { fetchPolicy: 'cache-only' } // We handle the polling/loading in ProjectPage.tsx
  );

  // Downloads
  const downloadsQuery = projectsApiService.queries.useGetKeywordDownloads(
    { projectId: project?.id },
    { pollInterval: 10000 }
  );

  const match = useRouteMatch();
  const onSettingsPage = !!match.path?.includes(ProjectTabChoices.Settings);
  return (
    <VoxelAppBar
      className={`relative select-none ${className}`}
      bgClass={'bg-white'}
      shadow={isScrolling && !drawerOpen}
      border
    >
      <div className="flex flex-row items-center gap-4 w-full pl-0 h-14">
        {/* Logo */}
        <VoxelTooltipShort message="Projects home">
          <a
            href="/"
            onClick={(event) => {
              event.preventDefault();
              goToProjects();
            }}
          >
            <LogoSymbolsOnly height={4 * 4.5} />
          </a>
        </VoxelTooltipShort>

        <div className="flex flex-col justify-end gap-0.5 -ml-1 h-14">
          <div className="pl-0.5 w-fit -mb-1">
            <ProjectNameIndicator project={project} />
          </div>
          <div className="flex flex-row gap-0">
            <Route
              path={`/${project.id}/:subpage?`}
              render={(subProps) => (
                <div className={'h-full flex flex-row items-center'}>
                  <VoxelUnderlinedTabs
                    transition={false}
                    size="sm"
                    highlight="primary"
                    removeBorder
                    menuListItems={[
                      { name: 'Keywords', id: ProjectTabChoices.Manage },
                      { name: 'Insights', id: ProjectTabChoices.Analyze },
                      { name: 'Content plan', id: ProjectTabChoices.Plan },
                      { name: 'Rank tracking', id: ProjectTabChoices.Progress },
                    ]}
                    selectedId={subProps.match.params.subpage}
                    setSelectedId={(id) => {
                      if (id === ProjectTabChoices.Analyze) {
                        subProps.history.push(
                          `/${project.id}/${ProjectTabChoices.Analyze}/Market demand`
                        );
                      } else {
                        subProps.history.push(`/${project.id}/${id}`);
                      }
                    }}

                  />
                </div>
              )}
            />
          </div>
        </div>

        <div className="flex-grow" />

        <div className="flex flex-row items-center gap-2">
          <ProjectDomainIndicator project={project} />
          <LanguageIndicator project={project} />
          <LocationIndicator project={project} />
          <div />
          <VoxelAppBarDivider />
          <div />
          <StatusIndicator projectStatus={loadingResults} user={user} projectId={project.id} />
          {!!downloadsQuery?.result.length && (
            <VoxelPopover enableArrow offsetPx={16}>
              <VoxelPopoverTrigger enableActive>
                <VoxelIconButton
                  size="md"
                  color="white"
                  icon={
                    <FontAwesomeIcon
                      icon={downloadOpen ? faDownload : faDownloadOutlined}
                      size="lg"
                    />
                  }
                  textColorClass="opacity-70"
                  hoverTextColorClass="hover:opacity-90"
                  tooltip="Downloads"
                />
              </VoxelPopoverTrigger>
              <VoxelPopoverContent shadowClass="drop-shadow-lg">
                <DownloadsPopover downloads={downloadsQuery?.result} />
              </VoxelPopoverContent>
            </VoxelPopover>
          )}
          <VoxelIconButton
            size="sm"
            color="white"
            onClick={goToSettings}
            icon={<FontAwesomeIcon icon={onSettingsPage ? faCog : faCogOutlined} size="lg" />}
            textColorClass="opacity-70"
            hoverTextColorClass="hover:opacity-90"
            tooltip="Project settings"
          />
        </div>
      </div>
    </VoxelAppBar>
  );
};

export default ProjectAppBar;
